"use client";

import { PrintfulPurchase } from "@/components/marketplace/printful-purchase";
import { PressingPreorder } from "@/components/marketplace/pressing-preorder";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import Image from "next/image";
import { ItemArtwork } from "@/components/marketplace/item-artwork";
import Link from "next/link";
import { ShoppingCart, MessageSquare, ShieldCheck, Tag, Box, Disc, Mic2, ArrowLeft, Loader2, Music2, Play, Pencil, Check, X, Plus, Trash2, ImageIcon, Video, Volume2, FileDown, GripVertical, Link as LinkIcon, Film, UploadCloud, DollarSign, Flag, Youtube, BookOpen, Globe, Send, Package, ExternalLink, Palette, Type, ChevronDown, ChevronUp, Lock, AppWindow } from "lucide-react";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { ShareButton } from "@/components/ui/share-button";
import { QRShareButton } from "@/components/ui/qr-share-button";
import React, { useState, useEffect, use, useRef } from "react";
import { db } from "@/lib/firebase";
import { getMarketplaceItemById, updateMarketplaceItem } from "@/services/marketplace";
import SaveButton, { SaveIconButton } from "@/components/save-button";
import { redirectToCheckout } from "@/services/stripe";
import type { Item } from "@/types/marketplace";
import { useAuth } from "@/contexts/auth-context";
import { useAdmin } from "@/hooks/use-admin";
import { getFormatLabel } from "@/components/media/download-button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminToolbar from "@/components/admin/admin-toolbar";
import { addDoc, collection, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { deleteMarketplaceItem, toggleMarketplaceAdminHidden, getAllMedia } from "@/services/admin";
import { uploadFile } from "@/services/storage";
import ClaimCreditsButton from "@/components/claim-credits-button";
import { MediaCreditsPanel } from "@/components/media/media-credits-panel";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useIsSwedishUser } from "@/hooks/use-swedish-user";
import { SwishCheckoutDialog } from "@/components/payment/swish-checkout-dialog";
import { trackMediaEvent } from "@/services/stats";
import { ProductLD } from "@/components/seo/json-ld";
import { LinkedSessionBadge } from "@/components/artwork/linked-session-badge";
import { SponsorButton } from "@/components/payment/sponsor-button";

export default function MarketplaceItemPage({ params }: { params: Promise<{ id: string }> }) {
  const resolvedParams = use(params);
  const { user } = useAuth();
  const { isAdmin, isSuperAdmin } = useAdmin();
  const router = useRouter();
  const [item, setItem] = useState<Item | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isBuying, setIsBuying] = useState(false);
  // PWYW gate for web-instrument items: the embedded app is locked until the
  // user owns it / it's free / they've paid (an appAccess record from a verified
  // Stripe payment). null = still checking.
  const [hasInstrumentAccess, setHasInstrumentAccess] = useState<boolean | null>(null);

  // Inline editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editCurrency, setEditCurrency] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Media editing state
  const [editMedia, setEditMedia] = useState<{ order: number; url: string; type: 'image' | 'video' | 'audio'; label?: string }[]>([]);
  const [newMediaUrl, setNewMediaUrl] = useState('');
  const [newMediaType, setNewMediaType] = useState<'image' | 'video' | 'audio'>('image');
  const [newMediaLabel, setNewMediaLabel] = useState('');
  const [mediaUploading, setMediaUploading] = useState(false);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [vaultItems, setVaultItems] = useState<any[]>([]);
  const [vaultLoading, setVaultLoading] = useState(false);
  const mediaFileInputRef = useRef<HTMLInputElement>(null);

  // Download files editing state
  const [editDownloads, setEditDownloads] = useState<{ url: string; fileName: string; format?: string }[]>([]);
  const [newDownloadUrl, setNewDownloadUrl] = useState('');
  const [newDownloadName, setNewDownloadName] = useState('');
  const [downloadUploading, setDownloadUploading] = useState(false);
  const downloadFileInputRef = useRef<HTMLInputElement>(null);

  // Links / Resources / Courses editing state
  const [editLinks, setEditLinks] = useState<{ url: string; label: string; type: 'course' | 'class' | 'youtube' | 'resource' | 'other' }[]>([]);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');
  const [newLinkType, setNewLinkType] = useState<'course' | 'class' | 'youtube' | 'resource' | 'other'>('resource');

  // Delivery method state
  const [editDelivery, setEditDelivery] = useState<string>('download');

  // PWYW (Name Your Price) state
  const [pwywOpen, setPwywOpen] = useState(false);
  const [pwywAmount, setPwywAmount] = useState('');
  const [pwywProcessing, setPwywProcessing] = useState(false);
  const tm = useTranslations('marketplace');
  const tCommon = useTranslations('common');
  // Pay-what-you-feel downloads: the files show after the price window
  // (Gordon 2026-10-31: "download button has to be after the Pay What You
  // feel window") — chosen €0 here, or back from checkout with ?paid=.
  const [dlUnlocked, setDlUnlocked] = useState(false);
  const [dlThanks, setDlThanks] = useState(false);
  // A buyer (appAccess record) sees the files on every visit.
  const [fileAccess, setFileAccess] = useState(false);
  // Back from checkout: the server verifies the Stripe session before the
  // files show — a ?paid= in the URL alone unlocks nothing.
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('paid');
    if (!sessionId) return;
    fetch('/api/marketplace/grant-access', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ sessionId }) })
      .then(r => r.json())
      .then(d => {
        const id = resolvedParams.id;
        if (d?.ok && (Array.isArray(d.paid) ? d.paid : []).includes(id)) { setDlUnlocked(true); setDlThanks(true); }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swish state
  const isSwedish = useIsSwedishUser();
  const [swishOpen, setSwishOpen] = useState(false);
  const [swishAmountSEK, setSwishAmountSEK] = useState(0);

  // Report state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetail, setReportDetail] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportDone, setReportDone] = useState(false);

  // Thumbnail overlay (shown when no real image)
  const [overlayBg, setOverlayBg] = useState('#000000');
  const [overlayFontColor, setOverlayFontColor] = useState('#ffffff');
  const [overlayFontFamily, setOverlayFontFamily] = useState('system-ui');
  const [overlayFontSize, setOverlayFontSize] = useState<'auto' | number>('auto');
  const [overlayEditOpen, setOverlayEditOpen] = useState(false);
  const [savingOverlay, setSavingOverlay] = useState(false);

  useEffect(() => {
    async function loadItem() {
      try {
        const data = await getMarketplaceItemById(resolvedParams.id);
        setItem(data);
        if (data) {
          trackMediaEvent({
            type: 'view',
            entityType: 'marketplace',
            entityId: resolvedParams.id,
            entityTitle: data.title,
          });
        }
      } catch (err) {
        console.error("Error loading marketplace item:", err);
      } finally {
        setIsLoading(false);
      }
    }
    loadItem();
  }, [resolvedParams.id]);

  // Load overlay settings when item loads
  useEffect(() => {
    if (!item) return;
    const o = (item as any).thumbnailOverlay;
    if (o) {
      setOverlayBg(o.bgColor || '#000000');
      setOverlayFontColor(o.fontColor || '#ffffff');
      setOverlayFontFamily(o.fontFamily || 'system-ui');
      setOverlayFontSize(o.fontSize ?? 'auto');
    }
  }, [item]);

  const handleBuyNow = async () => {
    if (!item) return;
    setIsBuying(true);
    try {
      const priceInSmallestUnit = Math.round((item.price || 0) * 100);
      const imageUrl = images[0] !== 'https://placehold.co/800x800.png' ? images[0] : undefined;
      await redirectToCheckout([{
        itemId: item.itemId || resolvedParams.id,
        title: item.title,
        description: item.description?.substring(0, 200),
        price: priceInSmallestUnit,
        currency: item.currency || 'EUR',
        quantity: 1,
        imageUrl,
      }], undefined, user?.uid, isDigitalItem || dlFiles.length > 0 ? { returnPath: `/marketplace/${item.itemId || resolvedParams.id}` } : undefined);
    } catch (err) {
      console.error('Checkout error:', err);
      setIsBuying(false);
    }
  };

  useEffect(() => {
    if (!item || !user?.uid) { setFileAccess(false); return; }
    const itemId = item.itemId || resolvedParams.id;
    getDoc(doc(db, 'appAccess', `${user.uid}_${itemId}`))
      .then(snap => setFileAccess(snap.exists()))
      .catch(() => setFileAccess(false));
  }, [item, user, resolvedParams.id]);

  // Resolve gated-app access (owner / admin / free / paid) for embedded web
  // instruments AND downloadable desktop apps (software_license).
  useEffect(() => {
    if (!item || (item.type !== 'web_instrument' && item.type !== 'software_license')) { setHasInstrumentAccess(true); return; }
    const isOwner = !!(user?.uid && (item as any).seller?.userId === user.uid);
    const free = item.pricingModel === 'free';
    if (isOwner || isAdmin || isSuperAdmin || free) { setHasInstrumentAccess(true); return; }
    if (!user?.uid) { setHasInstrumentAccess(false); return; }
    const itemId = item.itemId || resolvedParams.id;
    getDoc(doc(db, 'appAccess', `${user.uid}_${itemId}`))
      .then(snap => setHasInstrumentAccess(snap.exists()))
      .catch(() => setHasInstrumentAccess(false));
  }, [item, user, isAdmin, isSuperAdmin, resolvedParams.id]);

  const handleNameYourPrice = async () => {
    if (!item) return;
    const amount = parseFloat(pwywAmount || '0');
    if (isNaN(amount) || amount < 0) return;
    if (amount === 0 && zeroAllowed) {
      setDlUnlocked(true);
      setPwywOpen(false);
      startDownload();
      return;
    }
    setPwywProcessing(true);
    try {
      const minCents = item.minPrice || 0;
      const amountCents = Math.max(Math.round(amount * 100), minCents);
      const imageUrl = images[0] !== 'https://placehold.co/800x800.png' ? images[0] : undefined;
      await redirectToCheckout([{
        itemId: item.itemId || resolvedParams.id,
        title: item.title,
        description: item.description?.substring(0, 200),
        price: amountCents,
        currency: item.currency || 'EUR',
        quantity: 1,
        imageUrl,
      }], undefined, user?.uid, { returnPath: `/marketplace/${item.itemId || resolvedParams.id}` });
    } catch (err) {
      console.error('PWYW checkout error:', err);
      setPwywProcessing(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!reportReason || !item) return;
    setReportSubmitting(true);
    try {
      await addDoc(collection(db, 'reports'), {
        targetType: 'marketplace_item',
        targetId: item.itemId || resolvedParams.id,
        targetTitle: item.title || '',
        targetUrl: `/marketplace/${resolvedParams.id}`,
        reason: reportReason,
        detail: reportDetail.trim(),
        reportedBy: user?.uid || 'anonymous',
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setReportDone(true);
      setTimeout(() => {
        setReportOpen(false);
        setReportDone(false);
        setReportReason('');
        setReportDetail('');
      }, 2000);
    } catch (err) {
      console.error('Report submission error:', err);
    } finally {
      setReportSubmitting(false);
    }
  };

  // Determine if current user can edit this item
  const canEdit = item && user?.uid && (
    isSuperAdmin || isAdmin || item.seller?.userId === user.uid
  );

  const isDigitalDownload = item?.type === 'digital_download';
  // No set price = pay what you feel (Gordon 2026-10-31: "Price on request …
  // is wrong, it should be Pay what you feel for items with no price set").
  // "free" = a maker's free product BB links to (e.g. MorningDAW by Morningdew
  // Media). It's never sold here, so no pay-what-you-feel on it (Gordon 2026-11-01).
  const isFree = item?.pricingModel === 'free';
  const isPWYW = !isFree && (item?.pricingModel === 'pay_what_you_want' || item?.pricingModel === 'pwyw' as any || !(Number(item?.price) > 0));
  const zeroAllowed = isPWYW && !((item?.minPrice || 0) > 0);
  const dlFiles: { url: string; fileName?: string }[] = (item as any)?.downloadFiles?.length
    ? (item as any).downloadFiles
    : (item?.type === 'digital_download' && (item as any)?.webInstrument?.url ? [{ url: (item as any).webInstrument.url }] : []);
  // Digital content: files AND links stay hidden until paid (Gordon
  // 2026-10-31: "the files and links can't be visible until after you pay")
  // — or until €0 is chosen where pay-what-you-feel allows it. The seller and
  // admins always see them.
  const isDigitalItem = ['digital_download', 'software_license', 'video_access', 'course', 'bundle', 'web_instrument'].includes(item?.type as string);
  const isItemOwner = !!(user?.uid && (item as any)?.seller?.userId === user.uid);
  const contentOpen = dlUnlocked || fileAccess || isItemOwner || isAdmin || isSuperAdmin;
  // A free item BB only links to is never gated, sold or credit-claimed — it
  // points to the maker's own page (Gordon 2026-11-01, Scaler Detector).
  const linkedFrom = isFree ? ((item as any)?.webInstrument as { url?: string; creator?: string } | undefined) : undefined;
  const isLinkedFree = !!(isFree && linkedFrom?.url && linkedFrom?.creator);
  const dlGated = !isFree && dlFiles.length > 0 && (isPWYW || isDigitalItem) && !contentOpen;
  const linksGated = !isFree && isDigitalItem && !contentOpen;
  const startDownload = () => {
    const f = dlFiles[0];
    if (!f?.url) return;
    const a = document.createElement('a');
    a.href = f.url; a.rel = 'noopener'; if (f.fileName) a.download = f.fileName;
    document.body.appendChild(a); a.click(); a.remove();
  };
  // Show Swish when: user is Swedish + item currency is SEK
  const showSwish = isSwedish && (item?.currency || '').toUpperCase() === 'SEK';

  const startEditing = () => {
    if (!item) return;
    setEditTitle(item.title || '');
    setEditDescription((item as any).description || '');
    setEditPrice(item.price?.toString() || '');
    setEditCurrency(item.currency || 'EUR');
    setEditCategory(item.categoryKey || '');
    setEditTags(item.tags?.join(', ') || '');
    // Load media items
    const mediaItems = (item.media || []).map((m: any, i: number) => ({
      order: typeof m === 'string' ? i : (m.order ?? i),
      url: typeof m === 'string' ? m : m.url,
      type: (typeof m === 'string' ? 'image' : m.type || 'image') as 'image' | 'video' | 'audio',
      label: typeof m === 'string' ? '' : (m.label || ''),
    }));
    setEditMedia(mediaItems);
    // Load download files
    setEditDownloads((item as any).downloadFiles || []);
    // Load links
    setEditLinks((item as any).links || []);
    // Load delivery method
    setEditDelivery((item as any).deliveryMethod || 'download');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
  };

  const addMediaItem = () => {
    if (!newMediaUrl.trim()) return;
    setEditMedia(prev => [...prev, {
      order: prev.length,
      url: newMediaUrl.trim(),
      type: newMediaType,
      label: newMediaLabel.trim() || undefined,
    }]);
    setNewMediaUrl('');
    setNewMediaLabel('');
  };

  const removeMediaItem = (index: number) => {
    setEditMedia(prev => prev.filter((_, i) => i !== index).map((m, i) => ({ ...m, order: i })));
  };

  const handleMediaFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !item) return;
    setMediaUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `marketplace/${item.itemId}/media-${Date.now()}.${ext}`;
      const url = await uploadFile(file, path);
      setEditMedia(prev => [...prev, { order: prev.length, url, type: 'image', label: '' }]);
    } catch { /* silently fail */ } finally {
      setMediaUploading(false);
      if (mediaFileInputRef.current) mediaFileInputRef.current.value = '';
    }
  };

  const openVault = async () => {
    setVaultOpen(true);
    setVaultLoading(true);
    try {
      const data = await getAllMedia();
      setVaultItems(data.filter((m: any) =>
        m.coverArtUrl || m.imageUrl || m.artworkUrl || m.thumbnailUrl || m.thumbnail || m.image
      ));
    } catch { /* empty */ } finally { setVaultLoading(false); }
  };

  // YouTube helpers
  const getYouTubeId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    return match ? match[1] : null;
  };
  const isYouTubeUrl = (url: string): boolean => !!getYouTubeId(url);

  const addDownloadFile = () => {
    if (!newDownloadUrl.trim() || !newDownloadName.trim()) return;
    const ext = newDownloadName.split('.').pop()?.toLowerCase() || '';
    setEditDownloads(prev => [...prev, {
      url: newDownloadUrl.trim(),
      fileName: newDownloadName.trim(),
      format: ext,
    }]);
    setNewDownloadUrl('');
    setNewDownloadName('');
  };

  const handleDownloadFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !item) return;
    setDownloadUploading(true);
    try {
      const path = `marketplace/${item.itemId}/downloads/${Date.now()}-${file.name}`;
      const url = await uploadFile(file, path);
      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      setEditDownloads(prev => [...prev, { url, fileName: file.name, format: ext }]);
    } catch { /* silently fail */ } finally {
      setDownloadUploading(false);
      if (downloadFileInputRef.current) downloadFileInputRef.current.value = '';
    }
  };

  const addLink = () => {
    if (!newLinkUrl.trim()) return;
    // Auto-detect YouTube
    const detectedType = isYouTubeUrl(newLinkUrl.trim()) ? 'youtube' : newLinkType;
    setEditLinks(prev => [...prev, {
      url: newLinkUrl.trim(),
      label: newLinkLabel.trim() || newLinkUrl.trim(),
      type: detectedType,
    }]);
    setNewLinkUrl('');
    setNewLinkLabel('');
  };

  const removeLink = (index: number) => {
    setEditLinks(prev => prev.filter((_, i) => i !== index));
  };

  const removeDownloadFile = (index: number) => {
    setEditDownloads(prev => prev.filter((_, i) => i !== index));
  };

  const saveEdits = async () => {
    if (!item) return;
    setIsSaving(true);
    try {
      const updates: Record<string, any> = {
        title: editTitle,
        description: editDescription,
        price: editPrice ? parseFloat(editPrice) : undefined,
        currency: editCurrency,
        categoryKey: editCategory,
        tags: editTags.split(',').map(t => t.trim()).filter(Boolean),
        media: editMedia.map((m, i) => ({ order: i, url: m.url, type: m.type, label: m.label || '' })),
        downloadFiles: editDownloads.length > 0 ? editDownloads : [],
        links: editLinks,
        deliveryMethod: editDelivery,
      };
      await updateMarketplaceItem(item.itemId, updates);
      // Refresh item
      const refreshed = await getMarketplaceItemById(item.itemId);
      if (refreshed) setItem(refreshed);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving marketplace item:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Box className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Product not found</h2>
          <p className="text-muted-foreground mb-4">This item doesn&apos;t exist or has been removed.</p>
          <Link href="/marketplace"><Button variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Back</Button></Link>
        </div>
      </div>
    );
  }

  // Get images from media array or fallback (only image type for gallery)
  const images: string[] = [];
  if (item.media && Array.isArray(item.media)) {
    for (const m of item.media) {
      if (typeof m === "string") {
        images.push(m);
      } else if (m && typeof m === "object" && m.url) {
        // Only add images to the gallery, not audio/video
        if (!m.type || m.type === 'image') {
          images.push(m.url);
        }
      }
    }
  }
  if (images.length === 0) {
    images.push("https://placehold.co/800x800.png");
  }

  const selectedImage = images[selectedImageIndex] || images[0];
  const priceDisplay = isFree ? tCommon('free') : item.price
    ? item.currency === "SEK"
      ? `${item.price} kr`
      : `€${Number(item.price).toFixed(2)}`
    : tm('payWhatYouFeel');

  // Parse tracklist and credits from description
  const description = (item as any).description || "";
  const tracklist = (item as any).tracklist as string[] | undefined;
  const credits = (item as any).credits as string[] | undefined;

  return (
    <div className="min-h-screen bg-background text-foreground w-full">
      <ProductLD
        name={item.title}
        description={item.description?.substring(0, 200)}
        imageUrl={(item as any).imageUrl || (item as any).thumbnailUrl || (item as any).images?.[0]}
        url={`${process.env.NEXT_PUBLIC_APP_URL || 'https://breakinbread.com'}/marketplace/${resolvedParams.id}`}
        price={item.price ? Math.round(item.price * 100) : undefined}
        currency={item.currency || 'EUR'}
        sellerName={(item.seller as any)?.name}
      />
      {/* Header */}
      <div className="border-b border-border/50 bg-background/95 backdrop-blur sticky top-[50px] z-30">
        <div className="w-full px-page py-3 flex items-center gap-3">
          <Breadcrumbs className="min-w-0 flex-1" items={[
            { navKey: 'marketplace', href: '/marketplace', icon: ShoppingCart },
            { label: item.title, icon: ShoppingCart },
          ]} />
          {(isAdmin || isSuperAdmin) && (item.itemId || resolvedParams.id) && (
            <LinkedSessionBadge entityId={item.itemId || resolvedParams.id} />
          )}
          <div className="ml-auto">
            <AdminToolbar
              entityType="Item"
              entityData={item as any}
              isHidden={(item as any).adminHidden}
              ownerId={item.seller?.userId}
              editFields={[
                { key: "title", label: "Title", type: "text" },
                { key: "description", label: "Description", type: "textarea" },
                { key: "imageUrl", label: "Thumbnail Image", type: "image", storageFolder: `marketplace/${item.itemId}` },
                { key: "price", label: "Price", type: "number" },
                { key: "currency", label: "Currency", type: "suggest", suggest: [{ collection: "item", field: "currency" }], extra: ["SEK", "EUR", "USD", "GBP", "NOK", "DKK"] },
                { key: "type", label: "Item Type", type: "select", options: [
                  { value: "digital", label: "Digital" },
                  { value: "physical", label: "Physical" },
                  { value: "service", label: "Service" },
                  { value: "beats", label: "Beats" },
                  { value: "samples", label: "Samples" },
                  { value: "merchandise", label: "Merchandise" },
                  { value: "other", label: "Other" },
                ]},
                { key: "pricingModel", label: "Pricing Model", type: "select", options: [
                  { value: "fixed", label: "Fixed Price" },
                  { value: "pay_what_you_want", label: "Name Your Price (PWYW)" },
                  { value: "free", label: "Free" },
                ]},
                { key: "category", label: "Category", type: "suggest", suggest: [{ collection: "item", field: "category" }] },
                { key: "status", label: "Status", type: "select", options: [
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                  { value: "sold_out", label: "Sold Out" },
                  { value: "archived", label: "Archived" },
                ]},
              ]}
              onToggleVisibility={async (hidden) => toggleMarketplaceAdminHidden(item.itemId, hidden)}
              onUpdate={async (data) => {
                await updateMarketplaceItem(item.itemId, data);
                return true;
              }}
              onDelete={async () => deleteMarketplaceItem(item.itemId)}
              onDeleteRedirect={() => router.push('/home')}
            />
          </div>
        </div>
      </div>

      <div className="w-full px-page py-6 lg:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Image Gallery */}
          <div className="lg:col-span-2">
            {/* Main image — constrained so it never exceeds the viewport height */}
            {(() => {
              const isPlaceholder = selectedImage === 'https://placehold.co/800x800.png';
              const autoFs = typeof overlayFontSize === 'number'
                ? overlayFontSize
                : Math.max(18, Math.min(80, Math.floor(460 / Math.max(1, item.title.length))));
              const fontFamilyMap: Record<string, string> = {
                'system-ui': 'system-ui, sans-serif',
                'serif': 'Georgia, serif',
                'mono': 'ui-monospace, monospace',
                'display': '"Helvetica Neue", Arial, sans-serif',
              };
              const resolvedFont = fontFamilyMap[overlayFontFamily] || overlayFontFamily;
              return (
                <div
                  className="relative overflow-hidden rounded-xl shadow-lg bg-muted"
                  style={{
                    width: '100%',
                    maxWidth: 'min(100%, calc(100vh - 200px))',
                    aspectRatio: '1 / 1',
                  }}
                >
                  {isPlaceholder && !(item as any).thumbnailOverlay ? (
                    // No image and no custom overlay saved → artwork from the
                    // linked product page, or generated vector art (Gordon 2026-10-31).
                    <ItemArtwork item={item} className="absolute inset-0" />
                  ) : isPlaceholder ? (
                    // Name overlay when no real image
                    <div
                      className="absolute inset-0 flex items-center justify-center p-8"
                      style={{ backgroundColor: overlayBg }}
                    >
                      <p
                        className="text-center leading-tight font-bold select-none"
                        style={{
                          color: overlayFontColor,
                          fontFamily: resolvedFont,
                          fontSize: `${autoFs}px`,
                          wordBreak: 'break-word',
                          hyphens: 'auto',
                        }}
                      >
                        {item.title}
                      </p>
                    </div>
                  ) : (
                    <Image src={selectedImage} alt={item.title} fill className="object-contain" />
                  )}
                </div>
              );
            })()}

            {/* Overlay customisation — admin only */}
            {(isAdmin || isSuperAdmin) && (
              <div className="mt-2 border border-border/50 rounded-lg overflow-hidden">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
                  onClick={() => setOverlayEditOpen(o => !o)}
                >
                  <Palette className="h-3.5 w-3.5" />
                  Thumbnail Overlay (shown when no image)
                  {overlayEditOpen ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
                </button>
                {overlayEditOpen && (
                  <div className="border-t border-border/50 p-3 bg-muted/20 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Background colour</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={overlayBg}
                            onChange={e => setOverlayBg(e.target.value)}
                            className="h-8 w-14 rounded cursor-pointer border border-border"
                          />
                          <input
                            type="text"
                            value={overlayBg}
                            onChange={e => setOverlayBg(e.target.value)}
                            className="flex-1 h-8 text-xs bg-background border border-input rounded px-2"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Text colour</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={overlayFontColor}
                            onChange={e => setOverlayFontColor(e.target.value)}
                            className="h-8 w-14 rounded cursor-pointer border border-border"
                          />
                          <input
                            type="text"
                            value={overlayFontColor}
                            onChange={e => setOverlayFontColor(e.target.value)}
                            className="flex-1 h-8 text-xs bg-background border border-input rounded px-2"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Font family</label>
                        <select
                          value={overlayFontFamily}
                          onChange={e => setOverlayFontFamily(e.target.value)}
                          className="w-full h-8 text-xs bg-background border border-input rounded px-2"
                        >
                          <option value="system-ui">System (default)</option>
                          <option value="serif">Serif</option>
                          <option value="mono">Monospace</option>
                          <option value="display">Display / Helvetica</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground block mb-1">Font size</label>
                        <div className="flex items-center gap-2">
                          <select
                            value={overlayFontSize === 'auto' ? 'auto' : 'custom'}
                            onChange={e => setOverlayFontSize(e.target.value === 'auto' ? 'auto' : 32)}
                            className="h-8 text-xs bg-background border border-input rounded px-2"
                          >
                            <option value="auto">Auto-fit</option>
                            <option value="custom">Custom px</option>
                          </select>
                          {overlayFontSize !== 'auto' && (
                            <input
                              type="number"
                              min={10}
                              max={200}
                              value={overlayFontSize}
                              onChange={e => setOverlayFontSize(Number(e.target.value))}
                              className="w-16 h-8 text-xs bg-background border border-input rounded px-2"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="w-full h-7 text-xs"
                      disabled={savingOverlay}
                      onClick={async () => {
                        setSavingOverlay(true);
                        try {
                          await updateMarketplaceItem(item.itemId, {
                            thumbnailOverlay: {
                              bgColor: overlayBg,
                              fontColor: overlayFontColor,
                              fontFamily: overlayFontFamily,
                              fontSize: overlayFontSize,
                            },
                          } as any);
                        } finally {
                          setSavingOverlay(false);
                        }
                      }}
                    >
                      {savingOverlay ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                      Save overlay settings
                    </Button>
                  </div>
                )}
              </div>
            )}

            {images.filter(img => img !== 'https://placehold.co/800x800.png').length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    className={`aspect-square relative rounded-lg overflow-hidden ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-all ${selectedImageIndex === idx ? "ring-2 ring-primary shadow-md" : "opacity-70 hover:opacity-100"}`}
                  >
                    {img === 'https://placehold.co/800x800.png' ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-muted">
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      </div>
                    ) : (
                      <Image src={img} alt={`${item.title} ${idx + 1}`} fill className="object-cover" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Audio/Video players for non-image media */}
            {item.media && item.media.filter((m: any) => (typeof m === 'object' && (m.type === 'audio' || m.type === 'video'))).length > 0 && (
              <div className="mt-4 space-y-2">
                {item.media.filter((m: any) => typeof m === 'object' && m.type === 'audio').map((m: any, idx: number) => (
                  <div key={`audio-${idx}`} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50">
                    <Volume2 className="h-4 w-4 text-primary flex-shrink-0" />
                    <span className="text-xs text-muted-foreground truncate flex-1">{m.label || `Audio ${idx + 1}`}</span>
                    <audio controls src={m.url} className="h-8 max-w-[200px]" />
                  </div>
                ))}
                {item.media.filter((m: any) => typeof m === 'object' && m.type === 'video').map((m: any, idx: number) => {
                  const ytId = getYouTubeId(m.url || '');
                  return (
                    <div key={`video-${idx}`} className="rounded-lg overflow-hidden">
                      <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                        {ytId ? <Youtube className="h-3 w-3 text-red-500" /> : <Video className="h-3 w-3" />}
                        {m.label || `Video ${idx + 1}`}
                      </p>
                      {ytId ? (
                        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                          <iframe
                            src={`https://www.youtube.com/embed/${ytId}`}
                            className="absolute inset-0 w-full h-full rounded-lg"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={m.label || `Video ${idx + 1}`}
                          />
                        </div>
                      ) : (
                        // A product film (autoplay set on the item) plays silently on a loop, inline on phones.
                        <video controls playsInline preload="metadata" src={m.url} poster={m.poster || undefined}
                          {...(m.autoplay ? { autoPlay: true, muted: true, loop: true } : {})}
                          className="w-full rounded-lg" />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Download files section */}
            {dlThanks && (
              <p role="status" className="mt-4 rounded-lg border-2 border-primary bg-primary/10 px-3 py-2 text-sm font-medium">{tm('pwywThanks')}</p>
            )}
            {(item as any).downloadFiles && (item as any).downloadFiles.length > 0 && !dlGated && (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileDown className="h-4 w-4 text-primary" />Download Files
                    {(item as any).deliveryMethod && (item as any).deliveryMethod !== 'download' && (
                      <Badge variant="outline" className="text-[10px] ml-auto">
                        {(item as any).deliveryMethod === 'email' ? '📧 Sent via email' :
                         (item as any).deliveryMethod === 'chat' ? '💬 Sent via chat' :
                         (item as any).deliveryMethod === 'sms' ? '📱 Sent via SMS' : 'Manual delivery'}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(item as any).downloadFiles.map((dl: any, idx: number) => {
                    const fmt = dl.format || (dl.url ? getFormatLabel(dl.url, "FILE") : "FILE");
                    const sizeLabel = dl.sizeBytes
                      ? dl.sizeBytes >= 1_048_576
                        ? `${(dl.sizeBytes / 1_048_576).toFixed(1)} MB`
                        : `${(dl.sizeBytes / 1024).toFixed(0)} KB`
                      : null;
                    return (
                      <a key={idx} href={dl.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/50 text-sm group">
                        <FileDown className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="flex-1 truncate">{dl.fileName}</span>
                        {sizeLabel && (
                          <span className="text-[11px] text-muted-foreground shrink-0">{sizeLabel}</span>
                        )}
                        <Badge variant="outline" className="text-[10px] shrink-0 uppercase">{fmt}</Badge>
                      </a>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Links / Resources / Courses section */}
            {(item as any).links && (item as any).links.length > 0 && !linksGated && (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><BookOpen className="h-4 w-4 text-primary" />Links &amp; Resources</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(item as any).links.map((lk: any, idx: number) => {
                    const ytId = getYouTubeId(lk.url || '');
                    const icon = lk.type === 'youtube' || ytId ? <Youtube className="h-4 w-4 text-red-500 flex-shrink-0" /> :
                                 lk.type === 'course' ? <BookOpen className="h-4 w-4 text-blue-500 flex-shrink-0" /> :
                                 lk.type === 'class' ? <BookOpen className="h-4 w-4 text-purple-500 flex-shrink-0" /> :
                                 <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
                    if (ytId) {
                      return (
                        <div key={idx} className="space-y-1">
                          <p className="text-xs text-muted-foreground flex items-center gap-1">{icon}{lk.label}</p>
                          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                            <iframe src={`https://www.youtube.com/embed/${ytId}`}
                              className="absolute inset-0 w-full h-full rounded-lg"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen title={lk.label} />
                          </div>
                        </div>
                      );
                    }
                    return (
                      <a key={idx} href={lk.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded-md hover:bg-secondary/50 text-sm group">
                        {icon}
                        <span className="flex-1 truncate">{lk.label || lk.url}</span>
                        {lk.type && lk.type !== 'other' && <Badge variant="outline" className="text-[10px] capitalize flex-shrink-0">{lk.type}</Badge>}
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 flex-shrink-0" />
                      </a>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Media Management (edit mode) */}
            {isEditing && canEdit && (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Manage Media</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Existing media items */}
                  {editMedia.map((m, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-md bg-secondary/30">
                      <div className="flex-shrink-0">
                        {m.type === 'image' && <ImageIcon className="h-4 w-4 text-blue-500" />}
                        {m.type === 'video' && <Video className="h-4 w-4 text-purple-500" />}
                        {m.type === 'audio' && <Volume2 className="h-4 w-4 text-green-500" />}
                      </div>
                      <span className="text-xs truncate flex-1" title={m.url}>{m.label || m.url}</span>
                      <Badge variant="outline" className="text-xs flex-shrink-0">{m.type}</Badge>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeMediaItem(idx)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}

                  {/* Add new media */}
                  <div className="border border-dashed border-border rounded-md p-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Add Media</p>
                    <div className="flex gap-2">
                      <select
                        value={newMediaType}
                        onChange={(e) => setNewMediaType(e.target.value as any)}
                        className="flex h-9 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring w-20"
                      >
                        <option value="image">Image</option>
                        <option value="video">Video</option>
                        <option value="audio">Audio</option>
                      </select>
                      <Input
                        value={newMediaLabel}
                        onChange={(e) => setNewMediaLabel(e.target.value)}
                        placeholder="Label (optional)"
                        className="h-9 text-xs w-28"
                      />
                    </div>
                    {/* URL input row */}
                    <div className="flex gap-2">
                      <Input
                        value={newMediaUrl}
                        onChange={(e) => setNewMediaUrl(e.target.value)}
                        placeholder="https://... (paste URL)"
                        className="h-9 text-xs flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && addMediaItem()}
                      />
                      <Button size="sm" className="h-9 px-3" onClick={addMediaItem} disabled={!newMediaUrl.trim()} title="Add URL">
                        <Plus className="h-4 w-4" />
                      </Button>
                      {/* File upload button */}
                      <input ref={mediaFileInputRef} type="file" accept="image/*,video/*,audio/*" className="hidden" onChange={handleMediaFileUpload} />
                      <Button size="sm" variant="outline" className="h-9 px-3" title="Upload file"
                        onClick={() => mediaFileInputRef.current?.click()} disabled={mediaUploading}>
                        {mediaUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                      </Button>
                      {/* Media Vault picker */}
                      <Button size="sm" variant="outline" className="h-9 px-3" title="Pick from Media Vault" onClick={openVault}>
                        <Film className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Media Vault inline picker */}
                    {vaultOpen && (
                      <div className="border rounded-lg p-2 bg-card/80 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground">Pick from Media Vault</span>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setVaultOpen(false)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        {vaultLoading ? (
                          <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
                        ) : (
                          <div className="grid grid-cols-5 gap-1 max-h-40 overflow-y-auto">
                            {vaultItems.map((vi: any) => {
                              const imgUrl = vi.coverArtUrl || vi.imageUrl || vi.artworkUrl || vi.thumbnailUrl || vi.thumbnail || vi.image;
                              return (
                                <button key={vi.id} type="button"
                                  className="relative aspect-square rounded overflow-hidden border-2 border-transparent hover:border-primary transition-colors"
                                  title={vi.title || vi.name || 'Media item'}
                                  onClick={() => {
                                    setEditMedia(prev => [...prev, { order: prev.length, url: imgUrl, type: 'image', label: vi.title || '' }]);
                                    setVaultOpen(false);
                                  }}
                                >
                                  <Image src={imgUrl} alt={vi.title || ''} fill className="object-cover" />
                                </button>
                              );
                            })}
                            {vaultItems.length === 0 && <p className="col-span-5 text-center text-xs text-muted-foreground py-3">No images in vault</p>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Download Files Management (edit mode) */}
            {isEditing && canEdit && (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><FileDown className="h-4 w-4" />Download Files</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {editDownloads.map((dl, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-md bg-secondary/30">
                      <FileDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-xs truncate flex-1">{dl.fileName}</span>
                      {dl.format && <Badge variant="outline" className="text-xs">{dl.format}</Badge>}
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeDownloadFile(idx)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}

                  <div className="border border-dashed border-border rounded-md p-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Add Download File</p>
                    <Input
                      value={newDownloadName}
                      onChange={(e) => setNewDownloadName(e.target.value)}
                      placeholder="File name (e.g. album.zip)"
                      className="h-9 text-xs"
                    />
                    <div className="flex gap-2">
                      <Input
                        value={newDownloadUrl}
                        onChange={(e) => setNewDownloadUrl(e.target.value)}
                        placeholder="Paste URL or upload below"
                        className="h-9 text-xs flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && addDownloadFile()}
                      />
                      <Button size="sm" className="h-9" onClick={addDownloadFile} disabled={!newDownloadUrl.trim() || !newDownloadName.trim()} title="Add URL">
                        <Plus className="h-4 w-4" />
                      </Button>
                      <input ref={downloadFileInputRef} type="file" className="hidden" onChange={handleDownloadFileUpload} />
                      <Button size="sm" variant="outline" className="h-9 px-3" title="Upload file to Storage"
                        onClick={() => downloadFileInputRef.current?.click()} disabled={downloadUploading}>
                        {downloadUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  {/* Delivery method */}
                  <div className="pt-1">
                    <p className="text-xs font-medium text-muted-foreground mb-1.5">After-purchase delivery method</p>
                    <select value={editDelivery} onChange={e => setEditDelivery(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <option value="download">Direct download link (buyer downloads from page)</option>
                      <option value="email">Email (sent to buyer's email after purchase)</option>
                      <option value="chat">Chat (sent via in-app chat after purchase)</option>
                      <option value="sms">SMS (sent via SMS after purchase)</option>
                      <option value="manual">Manual (you deliver it manually)</option>
                    </select>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Links / Resources / Courses Management (edit mode) */}
            {isEditing && canEdit && (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2"><BookOpen className="h-4 w-4" />Links &amp; Resources</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {editLinks.map((lk, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-md bg-secondary/30">
                      {lk.type === 'youtube' || isYouTubeUrl(lk.url) ? <Youtube className="h-4 w-4 text-red-500 flex-shrink-0" /> :
                       lk.type === 'course' || lk.type === 'class' ? <BookOpen className="h-4 w-4 text-blue-500 flex-shrink-0" /> :
                       <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                      <span className="text-xs truncate flex-1" title={lk.url}>{lk.label || lk.url}</span>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0 capitalize">{lk.type}</Badge>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive flex-shrink-0" onClick={() => removeLink(idx)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}

                  <div className="border border-dashed border-border rounded-md p-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Add Link / Course / YouTube</p>
                    <div className="flex gap-2">
                      <select value={newLinkType} onChange={(e) => setNewLinkType(e.target.value as any)}
                        className="flex h-9 rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring w-24">
                        <option value="resource">Resource</option>
                        <option value="course">Course</option>
                        <option value="class">Class</option>
                        <option value="youtube">YouTube</option>
                        <option value="other">Other</option>
                      </select>
                      <Input value={newLinkLabel} onChange={(e) => setNewLinkLabel(e.target.value)}
                        placeholder="Label" className="h-9 text-xs w-28" />
                    </div>
                    <div className="flex gap-2">
                      <Input value={newLinkUrl} onChange={(e) => setNewLinkUrl(e.target.value)}
                        placeholder="https://... or youtube.com/watch?v=..." className="h-9 text-xs flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && addLink()} />
                      <Button size="sm" className="h-9 px-3" onClick={addLink} disabled={!newLinkUrl.trim()}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">YouTube URLs are automatically detected and embedded.</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Item Info & Actions */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <>
                        <Input
                          value={editCategory}
                          onChange={(e) => setEditCategory(e.target.value)}
                          placeholder="Category"
                          className="text-sm mb-2 h-8"
                        />
                        <Input
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          placeholder="Title"
                          className="text-2xl font-bold h-auto py-1"
                        />
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Tag className="h-4 w-4" />
                          <span>{item.categoryKey || item.type || "Music"}</span>
                        </div>
                        <CardTitle className="text-2xl sm:text-3xl font-bold">{item.title}</CardTitle>
                        {/* Who it belongs to — the artist and the label, linked to
                            their pages (Gordon 2026-11-02: more important than the
                            Printful store's name). */}
                        {(() => {
                          const it = item as any;
                          const artistId: string = it.artistId || it.artist?.docId || '';
                          const artistName: string = it.artistName || it.artist?.name || '';
                          const labelRef: string = it.brand?.labelId || it.brand?.slug || '';
                          const labelName: string = it.brand?.name || '';
                          if (!(artistId && artistName) && !(labelRef && labelName)) return null;
                          const chip = "inline-flex items-center gap-2 rounded-full border-2 border-foreground/60 px-3 py-1 text-sm font-semibold text-foreground transition-colors hover:border-foreground hover:bg-muted/40";
                          return (
                            <div className="flex flex-wrap gap-2 pt-3">
                              {artistId && artistName && (
                                <Link href={`/artists/${encodeURIComponent(artistId)}`} className={chip} aria-label={`${tm('itemArtist')}: ${artistName}`}>
                                  <Mic2 className="h-4 w-4" aria-hidden />
                                  <span className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">{tm('itemArtist')}</span>
                                  {artistName}
                                </Link>
                              )}
                              {labelRef && labelName && (
                                <Link href={`/labels/${encodeURIComponent(labelRef)}`} className={chip} aria-label={`${tm('itemLabel')}: ${labelName}`}>
                                  <Disc className="h-4 w-4" aria-hidden />
                                  <span className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">{tm('itemLabel')}</span>
                                  {labelName}
                                </Link>
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Share (BB members, chats, apps) + QR up by the title — the row
                        at the bottom sat far below a long pre-order panel. */}
                    {!isEditing && (
                      <>
                        <ShareButton iconOnly title={item.title} className="h-8 w-8 rounded-full" />
                        <QRShareButton title={item.title} imageUrl={(item.media?.[0] && typeof item.media[0] === "string" ? item.media[0] : (item.media?.[0] as any)?.url) || ""} className="h-8 w-8 px-0 rounded-full" />
                      </>
                    )}
                    {canEdit && !isEditing && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={startEditing} title="Edit item">
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {isEditing && (
                      <>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-destructive" onClick={cancelEditing} title="Cancel">
                          <X className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-green-600" onClick={saveEdits} disabled={isSaving} title="Save">
                          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </Button>
                      </>
                    )}
                    <SaveIconButton
                      itemId={item.itemId || resolvedParams.id}
                      itemType="item"
                      title={item.title || ''}
                      subtitle={item.seller?.organizationName || ''}
                      imageUrl={images[0] !== 'https://placehold.co/800x800.png' ? images[0] : ''}
                      link={`/marketplace/${item.itemId || resolvedParams.id}`}
                    />
                  </div>
                </div>
                {isEditing ? (
                  <Input
                    value={editTags}
                    onChange={(e) => setEditTags(e.target.value)}
                    placeholder="Tags (comma separated)"
                    className="text-xs mt-2 h-8"
                  />
                ) : (
                  item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-2">
                      {item.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  )
                )}
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={editPrice}
                      onChange={(e) => setEditPrice(e.target.value)}
                      placeholder="Price"
                      className="text-2xl font-bold h-auto py-1 w-32"
                      step="0.01"
                    />
                    <Input
                      value={editCurrency}
                      onChange={(e) => setEditCurrency(e.target.value)}
                      placeholder="Currency"
                      className="h-auto py-1 w-20 text-sm"
                    />
                  </div>
                ) : (
                  <p className="text-3xl sm:text-4xl font-bold text-primary">{priceDisplay}</p>
                )}
                <Separator className="my-4" />

                {/* Description */}
                {isEditing ? (
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Description"
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[120px]"
                  />
                ) : (
                  description && (
                    <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">{description}</p>
                  )
                )}

                {/* VST plugin / downloadable digital file with a hosted
                    download URL (Google Drive etc.). Shows a prominent
                    Download button + creator credit instead of an iframe —
                    iframes won't load Drive pages anyway. The
                    digital.formats array tells the user what they're
                    getting (e.g. VST3). */}
                {item.type === 'digital_download' && (item as any).webInstrument?.url && (() => {
                  const wi = (item as any).webInstrument;
                  const formats = Array.isArray((item as any).digital?.formats) ? (item as any).digital.formats : [];
                  const platform = (item as any).platform;
                  return (
                    <div className="mt-4 p-4 rounded-xl border border-primary/30 bg-primary/5 space-y-3">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="space-y-1">
                          <h3 className="text-sm font-semibold flex items-center gap-1.5">
                            <span>📦</span> Download
                          </h3>
                          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                            {formats.map((f: string) => (
                              <span key={f} className="px-1.5 py-0.5 rounded bg-muted font-mono">{f}</span>
                            ))}
                            {platform && <span>· {platform}</span>}
                            {(item as any).digital?.licenseType && <span>· {(item as any).digital.licenseType}</span>}
                          </div>
                        </div>
                        {dlGated ? (
                          <button
                            type="button"
                            onClick={() => (isPWYW ? setPwywOpen(true) : handleBuyNow())}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-semibold"
                          >
                            <DollarSign className="h-4 w-4" />{isPWYW ? tm('payWhatYouFeel') : tm('buyNow')}
                          </button>
                        ) : (
                          <a
                            href={wi.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-semibold"
                          >
                            {isLinkedFree ? tm('getFromMaker', { maker: wi.creator }) : tCommon('download')} ↗
                          </a>
                        )}
                      </div>
                      {isLinkedFree && (
                        <p className="text-xs text-muted-foreground">{tm('freeLinkNote', { maker: wi.creator })}</p>
                      )}
                      {wi.creator && (
                        <p className="text-xs text-muted-foreground">
                          By{' '}
                          {wi.creatorUrl ? (
                            wi.creatorUrl.startsWith('/')
                              ? <Link href={wi.creatorUrl} className="text-primary hover:underline">{wi.creator}</Link>
                              : <a href={wi.creatorUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{wi.creator}</a>
                          ) : (
                            <span>{wi.creator}</span>
                          )}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Web instrument — embedded launcher + credit. Renders an
                    iframe with the configured allow-list (microphone/midi/
                    autoplay) for tools that need device access. Gordon's
                    inaugural pair: Jonas Lund's JS-Kretz Sample Drum Machine
                    and Kretzonizer 4.0. */}
                {item.type === 'web_instrument' && (item as any).webInstrument?.url && (() => {
                  const wi = (item as any).webInstrument;
                  const aspectClass = wi.aspectRatio === '4:3' ? 'aspect-[4/3]'
                    : wi.aspectRatio === '1:1' ? 'aspect-square'
                    : wi.aspectRatio === 'auto' ? 'min-h-[600px]'
                    : 'aspect-video';
                  const allowAttr = Array.isArray(wi.iframeAllow) && wi.iframeAllow.length > 0
                    ? wi.iframeAllow.join('; ')
                    : 'autoplay';
                  return (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <h3 className="text-sm font-semibold flex items-center gap-1.5">
                          <span>🎛️</span> Launch in browser
                        </h3>
                        <a
                          href={wi.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline"
                        >
                          Open in new tab ↗
                        </a>
                      </div>
                      <div className={`relative w-full ${aspectClass} rounded-xl overflow-hidden border border-border bg-black shadow-lg`}>
                        {hasInstrumentAccess === false ? (
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6 bg-gradient-to-b from-zinc-900 to-black">
                            <Lock className="h-10 w-10 text-amber-400" />
                            <p className="text-base font-semibold text-white">This instrument is locked</p>
                            <p className="text-sm text-zinc-300 max-w-sm">
                              Pay what you feel to unlock it — then it&apos;s yours to play right here, any time.
                            </p>
                            <button
                              onClick={() => document.getElementById('buy-cta')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                              className="mt-1 px-5 py-2 rounded-full bg-amber-500 text-black text-sm font-semibold hover:bg-amber-400 transition-colors"
                            >
                              Name your price to unlock
                            </button>
                          </div>
                        ) : (
                          <iframe
                            src={wi.url}
                            title={item.title}
                            className="absolute inset-0 w-full h-full"
                            allow={allowAttr}
                            allowFullScreen
                            loading="lazy"
                          />
                        )}
                      </div>
                      {wi.creator && (
                        <p className="text-xs text-muted-foreground">
                          Created by{' '}
                          {wi.creatorUrl ? (
                            <a
                              href={wi.creatorUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline"
                            >
                              {wi.creator}
                            </a>
                          ) : (
                            <span>{wi.creator}</span>
                          )}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Desktop / OS app: gated download */}
                {item.type === 'software_license' && (item as any).webInstrument?.url && (() => {
                  const wi = (item as any).webInstrument;
                  return (
                    <div className="mt-4 space-y-2">
                      <h3 className="text-sm font-semibold flex items-center gap-1.5"><AppWindow className="h-4 w-4 text-primary" />Desktop app</h3>
                      {hasInstrumentAccess === false ? (
                        <div className="rounded-xl border border-border bg-gradient-to-b from-zinc-900 to-black p-6 text-center space-y-3">
                          <Lock className="h-8 w-8 text-amber-400 mx-auto" />
                          <p className="text-sm text-zinc-300 max-w-sm mx-auto">Pay what you feel to unlock the download — then it&apos;s yours to install.</p>
                          <button
                            onClick={() => document.getElementById('buy-cta')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                            className="px-5 py-2 rounded-full bg-amber-500 text-black text-sm font-semibold hover:bg-amber-400 transition-colors"
                          >
                            Name your price to unlock
                          </button>
                        </div>
                      ) : (
                        <a
                          href={wi.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
                        >
                          <FileDown className="h-4 w-4" /> Download app
                        </a>
                      )}
                      {isFree && wi.creator && (
                        <p className="text-xs text-muted-foreground">{tm('freeLinkNote', { maker: wi.creator })}</p>
                      )}
                      {wi.creator && (
                        <p className="text-xs text-muted-foreground">
                          By {wi.creatorUrl ? <a href={wi.creatorUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{wi.creator}</a> : <span>{wi.creator}</span>}
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Tracklist */}
                {tracklist && tracklist.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Music2 className="h-4 w-4 text-primary" />Tracklist</h3>
                    <div className="space-y-1">
                      {tracklist.map((track, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                          <span className="text-xs w-5 text-right flex-shrink-0">{idx + 1}.</span>
                          <span>{track}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Credits */}
                {credits && credits.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold mb-2">Credits</h3>
                    <div className="space-y-0.5">
                      {credits.map((credit, idx) => (
                        <p key={idx} className="text-xs text-muted-foreground">{credit}</p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Item details */}
                <div className="mt-4 space-y-2">
                  {item.type && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Type</span>
                      <span className="font-medium capitalize">{item.type.replace(/_/g, " ")}</span>
                    </div>
                  )}
                  {item.categoryKey && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Category</span>
                      <span className="font-medium capitalize">{item.categoryKey.replace(/_/g, " ")}</span>
                    </div>
                  )}
                  {item.currency && !isFree && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Currency</span>
                      <span className="font-medium">{item.currency}</span>
                    </div>
                  )}
                  {item.inventory?.trackQuantity && item.inventory.quantityAvailable !== undefined && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Stock</span>
                      <span className="font-medium">{item.inventory.quantityAvailable > 0 ? `${item.inventory.quantityAvailable} available` : "Sold out"}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="space-y-3">
              {/* Buy Now (fixed price) */}
              <span id="buy-cta" className="block scroll-mt-24" aria-hidden="true" />
              {/* A pressing on pre-order sells through its own panel (price floor + shipping zone). */}
              {item.pressingId && <PressingPreorder pressingId={item.pressingId} />}
              {!item.pressingId && item.type === 'pod' && item.pod?.provider === 'printful' && <PrintfulPurchase item={item}/>}
              {!item.pressingId && isLinkedFree && item.type !== 'web_instrument' && (
                <Button asChild size="lg" className="w-full text-lg gap-2">
                  <a href={linkedFrom!.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-5 w-5" />{tm('getFromMaker', { maker: linkedFrom!.creator! })}
                  </a>
                </Button>
              )}
              {!item.pressingId && !isPWYW && !isFree && !(item.type === 'pod' && item.pod?.provider === 'printful') && (
                showSwish ? (
                  <div className="flex gap-2">
                    <Button
                      size="lg"
                      className="flex-1 text-base gap-2 bg-[#00B67A] hover:bg-[#00a06d] text-white font-semibold"
                      onClick={() => {
                        const priceSEK = item?.price || 0;
                        setSwishAmountSEK(priceSEK);
                        setSwishOpen(true);
                      }}
                      disabled={isBuying}
                    >
                      Buy via Swish
                    </Button>
                    <Button size="lg" className="flex-1 text-base gap-2" onClick={handleBuyNow} disabled={isBuying} variant="outline">
                      {isBuying ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingCart className="h-5 w-5" />}
                      {isBuying ? 'Processing...' : 'Buy via Card'}
                    </Button>
                  </div>
                ) : (
                  <Button size="lg" className="w-full text-lg gap-2" onClick={handleBuyNow} disabled={isBuying}>
                    {isBuying ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShoppingCart className="h-5 w-5" />}
                    {isBuying ? 'Processing...' : 'Buy Now'}
                  </Button>
                )
              )}
              {/* Name Your Price */}
              {!item.pressingId && !isFree && !(item.type === 'pod' && item.pod?.provider === 'printful') && <Button
                size="lg"
                variant={isPWYW ? "default" : "secondary"}
                className="w-full text-lg gap-2"
                onClick={() => setPwywOpen(true)}
              >
                <DollarSign className="h-5 w-5" />
                {tm('payWhatYouFeel')}
              </Button>}
            </div>

            {/* Seller info */}
            {(item.seller?.organizationName || item.seller?.userId) && (
              <Card>
                <CardHeader className="flex flex-row items-center gap-3 space-y-0">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Disc className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{item.seller.organizationName || "Breakin' Bread"}</CardTitle>
                    <p className="text-xs text-muted-foreground">Official Store</p>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full gap-2"><MessageSquare className="h-4 w-4" /> Contact Seller</Button>
                </CardContent>
              </Card>
            )}

            {/* One left-aligned row, every button outlined and the same height
                (Gordon 2026-11-01: "messy buttons, left align … outline on
                angel and report"). */}
            <div className="flex flex-wrap items-center justify-start gap-2">
              <ShareButton title={item.title} variant="outline" size="sm" className="gap-1.5" />
              <QRShareButton title={item.title} imageUrl={(item.media?.[0] && typeof item.media[0] === "string" ? item.media[0] : (item.media?.[0] as any)?.url) || ""} />
              <SponsorButton
                kind="sponsor"
                entityType="marketplace"
                entityId={item.itemId || resolvedParams.id}
                entityTitle={item.title}
                recipientId={item.seller?.userId || ''}
                recipientName={(item.seller as any)?.name || item.seller?.organizationName || item.title}
                coverArtUrl={(item.media?.[0] && typeof item.media[0] === "string" ? item.media[0] : (item.media?.[0] as any)?.url) || ""}
                variant="outline"
              />
              {/* Nobody on BB can hold credits on a third party's free product. */}
              {!isLinkedFree && (
                <ClaimCreditsButton
                  mediaId={resolvedParams.id}
                  mediaType="marketplace"
                  mediaTitle={item.title || ""}
                />
              )}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setReportOpen(true)}><Flag className="h-4 w-4" /> {tCommon('report')}</Button>
            </div>

            {/* Credits & splits panel */}
            <div className="mt-6 pt-4 border-t border-border/50">
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wider">Credits & Splits</h3>
              <MediaCreditsPanel
                mediaId={resolvedParams.id}
                mediaType="work"
                mediaTitle={item.title || ""}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Name Your Price Dialog */}
      <Dialog open={pwywOpen} onOpenChange={setPwywOpen}>
        {/* Wide, one row and pinned to the top: on an iPad in landscape the
            keyboard covered the centred window (Gordon 2026-10-31). */}
        <DialogContent className="top-3 translate-y-0 sm:top-6 max-w-2xl">
          <DialogHeader>
            <DialogTitle>{tm('payWhatYouFeel')}</DialogTitle>
            <DialogDescription>
              {item?.minPrice ? `Minimum price: ${(item.minPrice / 100).toFixed(2)} ${item.currency || 'EUR'}` : tm('pwywHint')}
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const cur = (item?.currency || 'EUR').toUpperCase();
            const money = (v: number) => new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, maximumFractionDigits: v % 1 ? 2 : 0 }).format(v);
            const amount = Number(pwywAmount || 0);
            return (
              <>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Label htmlFor="pwyw-amount">{tm('pwywAmount')} ({cur})</Label>
                    <Input
                      id="pwyw-amount"
                      type="number"
                      inputMode="decimal"
                      min={item?.minPrice ? (item.minPrice / 100).toString() : "0"}
                      step="0.01"
                      placeholder="0.00"
                      value={pwywAmount}
                      onChange={e => setPwywAmount(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleNameYourPrice(); }}
                      autoFocus
                    />
                  </div>
                  <div className="flex gap-1.5" role="group" aria-label={tm('pwywAmount')}>
                    {(zeroAllowed ? [0, 2, 5, 10] : [2, 5, 10, 20]).map(v => (
                      <Button key={v} type="button" variant={pwywAmount !== '' && amount === v ? 'default' : 'outline'} className="h-10 min-w-12 px-3"
                        aria-pressed={pwywAmount !== '' && amount === v} onClick={() => setPwywAmount(String(v))}>
                        {money(v)}
                      </Button>
                    ))}
                  </div>
                </div>
                <DialogFooter className="flex-row flex-wrap justify-end gap-2">
                  <Button variant="outline" onClick={() => setPwywOpen(false)}>{tCommon('cancel')}</Button>
                  {showSwish && amount > 0 && (
                    <Button
                      className="bg-[#00B67A] hover:bg-[#00a06d] text-white font-semibold"
                      disabled={pwywProcessing}
                      onClick={() => {
                        if (isNaN(amount) || amount < 0) return;
                        const minCents = item?.minPrice || 0;
                        setSwishAmountSEK(Math.max(amount, minCents / 100));
                        setPwywOpen(false);
                        setSwishOpen(true);
                      }}
                    >
                      Pay via Swish
                    </Button>
                  )}
                  <Button onClick={handleNameYourPrice}
                    disabled={pwywProcessing || isNaN(amount) || amount < 0 || (amount === 0 && (!zeroAllowed || dlFiles.length === 0))}>
                    {pwywProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                    {amount > 0 ? tm('pwywPay', { amount: money(amount) }) : tCommon('download')}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Swish Checkout Dialog */}
      {item && (
        <SwishCheckoutDialog
          open={swishOpen}
          onOpenChange={setSwishOpen}
          amountSEK={swishAmountSEK}
          message={`Purchase: ${item.title}`}
          itemId={item.itemId || resolvedParams.id}
          itemType="marketplace"
          itemTitle={item.title}
          buyerId={user?.uid}
          buyerEmail={user?.email ?? undefined}
          onSuccess={() => {
            setSwishOpen(false);
          }}
        />
      )}

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Report this listing</DialogTitle>
            <DialogDescription>Let us know why this listing is problematic.</DialogDescription>
          </DialogHeader>
          {reportDone ? (
            <div className="py-6 text-center">
              <Check className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm font-medium">Thank you — your report has been submitted.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs mb-1 block">Reason</Label>
                <Select value={reportReason} onValueChange={setReportReason}>
                  <SelectTrigger><SelectValue placeholder="Select a reason…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spam">Spam or misleading</SelectItem>
                    <SelectItem value="copyright">Copyright violation</SelectItem>
                    <SelectItem value="inappropriate">Inappropriate content</SelectItem>
                    <SelectItem value="counterfeit">Counterfeit or fake item</SelectItem>
                    <SelectItem value="fraud">Fraud or scam</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Additional details (optional)</Label>
                <Textarea
                  placeholder="Describe the issue…"
                  value={reportDetail}
                  onChange={e => setReportDetail(e.target.value)}
                  className="min-h-[80px]"
                />
              </div>
            </div>
          )}
          {!reportDone && (
            <DialogFooter>
              <Button variant="outline" onClick={() => setReportOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitReport} disabled={!reportReason || reportSubmitting} variant="destructive">
                {reportSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Submit Report
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom spacer */}
      <div className="h-24" />
    </div>
  );
}
